import { Table, Typography } from "antd";
import React, { forwardRef, useCallback, useImperativeHandle, useMemo, useState } from "react";
import axios from "api/axios";
import {
    formatDataGeneral,
    getAttributeValue,
    getFirstFileLinkIfExist,
} from "utils/utils";
import { renderDpatEvidenceLink } from "utils/renderDpatEvidenceLink";
import { useDpatIndicatorDataSource } from "hooks/useDpatIndicatorDataSource";
import { isDpatScoreRelaxationExempt } from "utils/dpatScoreRelaxation";
import DpatSubIndicatorSection from "../setup/DpatSubIndicatorSection";
import DpatIndicatorCmsIntro from "../setup/DpatIndicatorCmsIntro";
import MaxScoreDisplay from "../setup/MaxScoreDisplay";
import ActualScoreDisplay from "../setup/ActualScoreDisplay";
import IndicatorScoreSection from "../setup/IndicatorScoreSection";
import Comment from "components/Comments";

const PROJECT_PROGRAM = "g3wMUKEMmH3";
const SAFEGUARD_STAGE = "b5nVLLk5sPU";
const SCREENING_DATA_ELEMENT = "wBJOB5CE6Du";
const REPORT_DATA_ELEMENT = "hM6AUNKRbKB";
const EPA_PERMIT_DATA_ELEMENT = "POdgz2rYh8H";

function fileUrl(eventId, dataElementUid) {
    return `https://dddp.gov.gh/api/events/files?eventUid=${eventId}&dataElementUid=${dataElementUid}`;
}

function getYearFromAttributeValue(value) {
    if (value == null || value === "" || value === "N/A") return null;
    const date = new Date(value);
    if (!Number.isNaN(date.getTime())) return date.getFullYear();
    const match = String(value).match(/\b(19|20)\d{2}\b/);
    return match ? Number(match[0]) : null;
}

function isProjectConcernedInSelectedYear(item, selectedYear) {
    const year = Number(selectedYear);
    if (!Number.isFinite(year)) return false;

    // Use actual dates (not expected ones).
    const startYear = getYearFromAttributeValue(
        getAttributeValue("Start Date", item),
    );
    const endYear = getYearFromAttributeValue(
        getAttributeValue("End Date", item),
    );

    // First check if startDate is in selectedYear, otherwise check endDate.
    const years = isDpatScoreRelaxationExempt() ? [year - 1, year] : [year];
    return years.some((activeYear) =>
        (startYear != null && startYear === activeYear)
        || (endYear != null && endYear === activeYear),
    );
}

function buildSafeguardProjectRows(instances, reports, selectedYear) {
    const projects = (formatDataGeneral(instances, "Project & Programme Type", "Project") || [])
        .filter((item) => isProjectConcernedInSelectedYear(item, selectedYear));
    const temp = [];

    projects.forEach((item, index) => {
        const reportLink = getFirstFileLinkIfExist(
            reports,
            REPORT_DATA_ELEMENT,
            item.trackedEntity,
            SAFEGUARD_STAGE,
        );
        const screeningLink = getFirstFileLinkIfExist(
            reports,
            SCREENING_DATA_ELEMENT,
            item.trackedEntity,
            SAFEGUARD_STAGE,
        );
        const epaLink = getFirstFileLinkIfExist(
            reports,
            EPA_PERMIT_DATA_ELEMENT,
            item.trackedEntity,
            SAFEGUARD_STAGE,
        );

        temp.push({
            key: item.trackedEntity || index + 1,
            no: index + 1,
            project: getAttributeValue("Name", item),
            startDate: getAttributeValue("Start Date", item) !== "N/A"
                ? getAttributeValue("Start Date", item)
                : "Not Provided",
            endDate: getAttributeValue("End Date", item) !== "N/A"
                ? getAttributeValue("End Date", item)
                : "Not Provided",
            screeming: screeningLink
                ? fileUrl(screeningLink, SCREENING_DATA_ELEMENT)
                : "Not Uploaded",
            report: reportLink
                ? fileUrl(reportLink, REPORT_DATA_ELEMENT)
                : "Not Uploaded",
            permit: epaLink
                ? fileUrl(epaLink, EPA_PERMIT_DATA_ELEMENT)
                : "Not Uploaded",
        });
    });

    let score1 = 1;
    let score2 = 2;
    let score3 = 1;

    for (const project of temp) {
        if (project.screeming === "Not Uploaded") {
            score1 = 0;
        }
        if (project.report === "Not Uploaded") {
            score3 = 0;
        }
        if (project.permit === "Not Uploaded") {
            score2 = 0;
        }
    }

    if (temp.length === 0) {
        score1 = 0;
        score2 = 0;
        score3 = 0;
    }

    return { projectList: temp, scorei: score1, scoreii: score2, scoreiii: score3 };
}

const EnvironmentalAndSocialSafeGuard = forwardRef(({
    year,
    data,
    districtId,
    hideComment,
    savedAssessmentData,
    assessmentStartLoading,
    isAssessmentStarted,
}, ref) => {
    const { Title } = Typography;
    const resolvedYear =
        year != null && typeof year === "object" && !Array.isArray(year)
            ? year.value ?? year.label
            : year;
    const ouId =
        typeof districtId === "string"
            ? districtId
            : districtId?.value?.id ?? districtId?.id ?? "";

    const [projectList, setProjectList] = useState([]);
    const [scorei, setScorei] = useState(0);
    const [scoreii, setScoreii] = useState(0);
    const [scoreiii, setScoreiii] = useState(0);
    const [maxScore, setMaxScore] = useState(4);

    useImperativeHandle(ref, () => ({
        getData: () => ({
            indicator: "PI1",
            area: "Annual Action Plan Implementation",
            maxScore,
            projectList,
            scorei,
            scoreii,
            scoreiii,
        }),
    }));

    const DPAT_SLUG = "pi1.0-1.5";

    const projectColumns = useMemo(() => [
        { title: "No", dataIndex: "no", key: "no" },
        { title: "Projects", dataIndex: "project", key: "project" },
        { title: "Start Date", dataIndex: "startDate", key: "startDate" },
        { title: "End Date", dataIndex: "endDate", key: "endDate" },
        {
            title: "Screening Froms",
            dataIndex: "screeming",
            key: "screeming",
            render: (value) => renderDpatEvidenceLink(value, "View Screening Form"),
        },
        {
            title: "EPA Permits",
            dataIndex: "permit",
            key: "permit",
            render: (value) => renderDpatEvidenceLink(value, "View Permit"),
        },
        {
            title: "Report",
            dataIndex: "report",
            key: "report",
            render: (value) => renderDpatEvidenceLink(value, "View Report"),
        },
    ], []);

    const loadFromDhis2 = useCallback(async () => {
        if (!ouId || resolvedYear == null) return;

        try {
            let instances = Array.isArray(data?.data) ? data.data : [];
            let reports = Array.isArray(data?.reports) ? data.reports : [];

            if (!instances.length) {
                const entitiesResp = await axios.get("/tracker/trackedEntities", {
                    params: {
                        orgUnit: ouId,
                        program: PROJECT_PROGRAM,
                        pageSize: 5000,
                    },
                });

                instances = entitiesResp?.data?.instances || [];

                if (instances.length) {
                    const eventsResp = await axios.get("/tracker/events", {
                        params: {
                            program: PROJECT_PROGRAM,
                            orgUnit: ouId,
                            pageSize: 10000,
                        },
                    });
                    reports = eventsResp?.data?.instances || [];
                }
            }

            const {
                projectList: rows,
                scorei: s1,
                scoreii: s2,
                scoreiii: s3,
            } = buildSafeguardProjectRows(instances, reports, resolvedYear);

            setProjectList(rows);
            setScorei(s1);
            setScoreii(s2);
            setScoreiii(s3);
        } catch (err) {
            console.error("EnvironmentalAndSocialSafeGuard fetch failed:", err);
            setProjectList([]);
            setScorei(0);
            setScoreii(0);
            setScoreiii(0);
        }
    }, [ouId, resolvedYear, data]);

    useDpatIndicatorDataSource({
        isAssessmentStarted,
        savedAssessmentData,
        assessmentStartLoading,
        loadFromDhis2,
        applySnapshot: (snap) => {
            setProjectList(snap?.projectList ?? []);
            setScorei(snap?.scorei ?? 0);
            setScoreii(snap?.scoreii ?? 0);
            setScoreiii(snap?.scoreiii ?? 0);
            setMaxScore(snap?.maxScore ?? 4);
        },
        deps: [year, districtId, data, loadFromDhis2],
    });

    return (
        <Comment
            data={projectList}
            year={resolvedYear}
            districtId={ouId}
            tableCommentedId={`pi1.0-1.5-${resolvedYear}`}
            hideComment={hideComment}
        >
            {({ renderCommentFeedbackFooter }) => (
          <DpatSubIndicatorSection code={DPAT_SLUG}>
                    <DpatIndicatorCmsIntro
                        year={resolvedYear}
                        slug={DPAT_SLUG}
                        mainTitleStyle={{ marginTop: "20px" }}
                    />

                    <IndicatorScoreSection>
                      <MaxScoreDisplay score={maxScore} />
                      <ActualScoreDisplay slug={DPAT_SLUG} label="PI 1.0-1.5i Actual Score" score={scorei} />
                      <ActualScoreDisplay slug={DPAT_SLUG} label="PI 1.0-1.5ii Actual Score" score={scoreii} />
                      <ActualScoreDisplay slug={DPAT_SLUG} label="PI 1.0-1.5iii Actual Score" score={scoreiii} />
                    </IndicatorScoreSection>

                    <Title level={5} style={{ marginTop: "20px" }}>
                        Evidence of environmental & social safeguards on projects
                    </Title>
                    <Table
                        columns={projectColumns}
                        dataSource={projectList}
                        pagination={false}
                        bordered
                        rowKey="key"
                    />
            {!hideComment && renderCommentFeedbackFooter()}
          </DpatSubIndicatorSection>
            )}
        </Comment>
    );
});

export default EnvironmentalAndSocialSafeGuard;
